@let isComponent = variant() === 'component';
@if (isComponent) {
  <div class="diff-container">
    @if (showCycleSeparator()) {
      <onyx-divider></onyx-divider>

      @if (cycleDiff(); as diff) {
        <span class="diff f-muted f-body-s">
          {{ diff | duration }}
        </span>
      }
    }
  </div>
}

@let isFirst =
  showFirstEmptyRoute() ||
  !position().isFirst ||
  (isReturningDelegationPoint() && position().isFirst);
@if (isComponent && !showCycleSeparator()) {
  @if (
    isFirst &&
    (isPoint() || position().isLast) &&
    !pointLegs().length &&
    isEmpty()
  ) {
    <div class="leg-line">
      @if (
        isReturningDelegationPoint() && status() === OrderPointStatus.TO_DO
      ) {
        <app-route-point-leg-line
          [status]="OrderPointStatus.COMPLETED"
          [compliance]="compliance()"
          [isEmpty]="true"
        ></app-route-point-leg-line>

        <app-route-point-leg-ride
          [compliance]="compliance()"
          [context]="context"
        ></app-route-point-leg-ride>
      }

      <app-route-point-leg-line
        [status]="status()"
        [compliance]="compliance()"
        [isEmpty]="isEmpty()"
      ></app-route-point-leg-line>
    </div>
  }

  @if (isFirst) {
    @for (leg of pointLegs(); track leg.polyline) {
      <app-route-point-leg
        [leg]="leg"
        [legs]="pointLegs()"
        [index]="position().index"
        [routeSummary]="routeSummary()"
        [subpointsRouteSummary]="subpointsRouteSummary()"
        [subpoints]="subpoints()"
        [subpointsExpanded]="subpointsExpanded()"
        [status]="status()"
        [compliance]="compliance()"
        [isPoint]="isPoint()"
        [isFirstLeg]="$first"
        [isLastLeg]="$last"
        [isFirstSubpoint]="isFirstSubpoint()"
        [isCurrent]="
          position().isCurrent &&
          status() === OrderPointStatus.TO_DO &&
          $index === offRouteLegIndex()
        "
        [isEmpty]="isEmpty()"
      ></app-route-point-leg>
    } @empty {
      @let value = this.value();
      <div class="custom-field f-muted">
        @if (calculatingRoute?.()) {
          {{ 'route.searching' | translate }}
        } @else if (
          RoutePointValueHelper.isOrderFormPoint(value) &&
          'address' in value &&
          !value.address
        ) {
          <app-route-point-route-summary
            [routeSummary]="null"
            [index]="position().index"
            [isFirstLeg]="false"
          ></app-route-point-route-summary>
        } @else if (isDelegation()) {
          <span class="f-body-s">
            @if (status() === OrderPointStatus.COMPLETED) {
              {{
                (isMidCycleDelegationEnd()
                  ? `${I18N_DELEGATION}.backToBaseMidCycle`
                  : `${I18N_DELEGATION}.backToBase`
                ) | translate
              }}
            } @else if (
              isReturningDelegationPoint() && returningLabels();
              as labels
            ) {
              <div class="returning-labels">
                @for (label of labels; track label.key) {
                  <div class="label">
                    <span class="f-muted">
                      {{ label.key | translate: label?.params }}
                    </span>

                    <span>·</span>

                    <onyx-link
                      color="blue"
                      (click)="
                        label.canReturn
                          ? openActionModal(label.delegations)
                          : cancelReturn(label.delegations)
                      "
                    >
                      {{
                        (label.canReturn
                          ? 'labels.beginReturn'
                          : 'labels.cancel'
                        ) | translate
                      }}
                    </onyx-link>
                  </div>
                }
              </div>
            }
          </span>
        } @else {
          <div class="route-error">
            {{ `${I18N}.calculateError` | translate }} ·

            <onyx-link color="blue" (click)="reportCalculateError()">
              {{ 'labels.contact' | translate }}
            </onyx-link>
          </div>
        }
      </div>
    }
  }
}

@let isPreview = variant() === 'preview';
@let isInteractive = isPreview && interactivePreview();
<div
  class="heading-container"
  [class.attached]="actionsAttached()"
  [class.expanded]="headingExpanded()"
  [class.tooltip]="isTooltip()"
>
  @if (!position().isFirst) {
    <div
      class="dissolve-effect"
      [class.subpoint]="!isPoint()"
      [class.preview]="isPreview"
      [style.width.px]="icon().size"
      [style.height.px]="icon().size"
    ></div>
  }

  <button
    class="heading row {{ previewColor() }}"
    [class.preview]="isPreview"
    [class.interactive]="isInteractive"
    [class.tooltip]="isTooltip()"
    (click)="isComponent || isInteractive ? toggleDetails() : null"
  >
    <app-route-point-marker
      [size]="icon().size"
      [icon]="icon().name"
      [expanded]="headingExpanded()"
      [point]="point()"
      [isPoint]="isPoint()"
      [compliance]="!isPreview ? compliance() : null"
      [letter]="letter()"
      [forceIcons]="forceIcons()"
      [markerColor]="markerColor()"
      [isDelegation]="isDelegation()"
    ></app-route-point-marker>

    @let showForm = state() === RoutePointState.FORM;
    <div class="row-header" [class.form]="showForm">
      @if (!showForm) {
        <onyx-address
          class="f-body-m f-semibold"
          [address]="startAddress()"
          [format]="isTooltip() || isInteractive ? 'zip-code' : 'city-flag'"
        ></onyx-address>

        <p class="f-body-xs f-muted">
          <ng-container *ngTemplateOutlet="typeTemplate"></ng-container>
        </p>
      }
      @if (showForm && form(); as form) {
        <h6>
          <ng-container *ngTemplateOutlet="typeTemplate"></ng-container>
        </h6>

        <div class="include-in-order">
          <onyx-toggle
            [formControl]="$any(form.controls).includeInOrder"
            label="labels.includeInOrder"
            [size]="32"
            (click)="$event.stopPropagation()"
          ></onyx-toggle>

          <onyx-tooltip
            tooltip="orders.orderForm.points.includeInOrderHint"
            (click)="$event.stopPropagation()"
          ></onyx-tooltip>
        </div>
      }

      <ng-template #typeTemplate>
        @if (isCanceled()) {
          {{ `${I18N}.pointCanceled` | translate }} ·
        }
        @if (isTechnicalPoint()) {
          @if (isDelegation()) {
            {{ `${I18N_DELEGATION}.${name()}` | translate }}
          } @else {
            {{
              `${DictionaryCode.TECHNICAL_POINT_CATEGORY}.${value().category}`
                | translate
            }}
          }
        } @else {
          {{ `${DictionaryCode.ORDER_POINT_TYPE}.${value().type}` | translate }}
        }

        @if (!['map', 'swapPanel'].includes(context) && typeIndex() != null) {
          {{ typeIndex() }}
        }

        @if (context === 'swapPanel' && order(); as order) {
          · {{ order.identifier }}
        }
      </ng-template>
    </div>

    @if (!isDelegation()) {
      @if (!showForm) {
        <div class="status-container">
          @let context = isTooltip() ? 'map' : 'order';
          @if (showTimeWindows()) {
            <app-route-point-time-windows
              [value]="value()"
              [context]="context"
            ></app-route-point-time-windows>
          } @else {
            <div
              class="status-row f-body-s f-medium {{
                isCanceled() ? '' : arrivalColor()
              }}"
            >
              @if (hasArrivalStatusIcons()) {
                <div class="status-icons">
                  @if (hasLoadedSemiTrailer()) {
                    <onyx-icon
                      name="semi-trailer-loaded-filled"
                      [size]="12"
                      onyxTooltip="labels.loadedAtPoint"
                    ></onyx-icon>
                  }

                  @if (waitingForServiceDurationWarning()) {
                    <onyx-icon
                      name="alert-two-tone-filled"
                      [size]="12"
                      [onyxTooltip]="`${I18N}.waitingForServiceDurationWarning`"
                    ></onyx-icon>
                  }

                  @if (hasArrivalTermConflict()) {
                    <onyx-icon
                      name="error-filled"
                      [size]="12"
                      [onyxTooltip]="`${I18N}.details.status.termConflictTooltip`"
                    ></onyx-icon>
                  }
                </div>

                <span class="f-body-s f-muted">·</span>
              }

              @if (isTodo()) {
                @if (estimations()?.arrivalTime; as arrivalTime) {
                  <span
                    [class.f-muted]="!arrivalColor()"
                    onyxTooltip="labels.estimatedArrivalTime"
                  >
                    {{ 'labels.eta' | translate }}
                  </span>

                  <span
                    [innerHTML]="
                      arrivalTime
                        | onyxDate: 'time-date-dot' : { separator: ' · ' }
                    "
                  ></span>
                } @else {
                  <span class="f-body-s f-muted">
                    {{ 'labels.unknownEta' | translate }}
                  </span>
                }
              } @else if (timestamps()?.arrivedAt; as arrivedAt) {
                <onyx-icon name="check-circle-filled" [size]="12"></onyx-icon>

                <span
                  [innerHTML]="
                    arrivedAt | onyxDate: 'time-date-dot' : { separator: ' · ' }
                  "
                ></span>
              }
            </div>

            @if (!isCanceled()) {
              <div class="status-row f-body-s f-medium {{ departureColor() }}">
                @if (hasDepartureTermConflict() && !isCompleted()) {
                  <onyx-icon
                    name="error-filled"
                    [size]="12"
                    [onyxTooltip]="`${I18N}.details.status.termConflictTooltip`"
                  ></onyx-icon>

                  <span class="f-body-s f-muted">·</span>
                }

                @if (!isCompleted()) {
                  @if (estimations()?.departureTime; as departureTime) {
                    <span
                      [class.f-muted]="!departureColor()"
                      onyxTooltip="labels.estimatedDepartureTime"
                    >
                      {{ 'labels.etd' | translate }}
                    </span>

                    <span
                      [innerHTML]="
                        departureTime
                          | onyxDate: 'time-date-dot' : { separator: ' · ' }
                      "
                    ></span>
                  } @else {
                    <span class="f-body-s f-muted">
                      {{ 'labels.unknownEtd' | translate }}
                    </span>
                  }
                } @else if (completedAt(); as completedAt) {
                  <onyx-icon name="check-circle-filled" [size]="12"></onyx-icon>

                  <span
                    [innerHTML]="
                      completedAt
                        | onyxDate: 'time-date-dot' : { separator: ' · ' }
                    "
                  ></span>
                }
              </div>
            }
          }
        </div>

        @let hasMove = hasMoveUp() || hasMoveDown();
        @if ((hasMove || showCenter() || options().length) && isComponent) {
          <button
            class="row-actions"
            tabindex="-1"
            (click)="$event.stopPropagation()"
          >
            @if (hasMove) {
              <onyx-icon-button
                variant="ghost"
                color="gray"
                [size]="32"
                icon="arrow-up-outlined"
                [circle]="true"
                [tabindex]="-1"
                [disabled]="!hasMoveUp()"
                onyxTooltip="labels.moveUp"
                (click)="hasMoveUp() ? movePoint(-1) : null"
              ></onyx-icon-button>

              <onyx-icon-button
                variant="ghost"
                color="gray"
                [size]="32"
                icon="arrow-down-outlined"
                [circle]="true"
                [tabindex]="-1"
                [disabled]="!hasMoveDown()"
                onyxTooltip="labels.moveDown"
                (click)="hasMoveDown() ? movePoint(1) : null"
              ></onyx-icon-button>
            }

            @if (showCenter()) {
              <onyx-icon-button
                variant="ghost"
                color="gray"
                [size]="32"
                icon="center-outlined"
                [circle]="true"
                [tabindex]="-1"
                onyxTooltip="labels.center"
                (click)="centerPoint()"
              ></onyx-icon-button>
            }

            @if (options().length) {
              <onyx-icon-button
                variant="ghost"
                color="gray"
                [size]="32"
                icon="options-horizontal-filled"
                [circle]="true"
                [tabindex]="-1"
                [onyxDropdownOptions]="options()"
                [(onyxDropdownAttached)]="actionsAttached"
                onyxTooltip="labels.options"
                (onyxDropdownSelectedValueChange)="
                  $event.value?.(); actionsAttached.set(false)
                "
              ></onyx-icon-button>
            }
          </button>
        }
      } @else {
        <button
          class="form-actions"
          tabindex="-1"
          (click)="$event.stopPropagation()"
        >
          <onyx-icon-button
            variant="transparent"
            color="gray"
            [size]="32"
            icon="undo-outlined"
            [circle]="true"
            [disabled]="!formHasChanges()"
            onyxTooltip="labels.undoChanges"
            (click)="formHasChanges() ? undoFormChanges() : null"
          ></onyx-icon-button>

          @if (showRemove()) {
            <onyx-icon-button
              variant="transparent"
              color="gray"
              [size]="32"
              icon="delete-outlined"
              [circle]="true"
              [disabled]="!hasRemove()"
              onyxTooltip="labels.delete"
              (click)="hasRemove() ? removePoint() : null"
            ></onyx-icon-button>
          }
        </button>
      }
    }
  </button>

  @if (headingExpanded()) {
    <div class="container row" [class.component]="variant() === 'component'">
      @if (
        [RoutePointState.DETAILS, RoutePointState.STATUS].includes(state())
      ) {
        @if (state() === RoutePointState.STATUS) {
          <app-route-point-status-form
            [point]="point()"
            [order]="order()"
          ></app-route-point-status-form>
        }

        <app-route-point-details
          [point]="point()"
          [goods]="goods$() | async"
          [compliance]="compliance()"
          [typeIndex]="typeIndex()"
          [isTooltip]="isTooltip()"
          [isPreview]="isPreview"
        ></app-route-point-details>
      } @else if (state() === RoutePointState.FORM && form(); as form) {
        <app-route-point-form
          [form]="form"
          [category]="value().category"
          [disabled]="!hasEdit()"
        ></app-route-point-form>
      }
    </div>
  }
</div>
